import React, { useContext, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import MCard, { MCardControlMethods } from "components/Shared/MCard.tsx";
import SinglePhoto from "components/Photos/PhotoTool/SinglePhoto/index.tsx";
import MGallery from "components/Photos/PhotoTool/Gallery/index.tsx";
import PhotoSettings from "components/Photos/PhotoTool/Settings";
import { loadPreference } from "actions/preferences";
import 
{
  PhotoButtonSetting,
  PhotoNumberingSetting,
  PhotoSizeSetting,
  PhotoTagSetting
} from "typeConstants/preferenceTypes";
import { MSelect, MSpinner } from "components/Shared";
import { CaseHeaderDto, Photo, UserRight } from "interfaces/Mueller.Business.Models.ts";
import {
  downloadCasePhotos,
  getCaseDeletedPhotos,
  getCasePhotos,
  restorePhotoFromTrash,
  savePhotoEdit,
  savePhotosSortOrder,
  updateMultiplePhotoProperties,
  updatePhotoProperties
} from "requests/photos/index.ts";
import { PHOTO_WRITE_UNRESTRICTED } from "typeConstants/rights";
import { userHasRight } from "selectors/rights";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider.tsx";
import {
  PhotoDownloadNamingConventions,
  photoEditOperationTypes,
  PhotoPropertiesPhotoSvc,
  PHOTOS_BROADCAST_CHANNEL_NAME,
  PHOTOS_BROADCAST_PHOTO_EDITED_TYPE,
  PHOTOS_BROADCAST_RELOAD_PHOTOS,
  PHOTOS_BROADCAST_SAVE_PHOENIX_FORMS
} from "typeConstants/photos/photos.ts";
import responseHandler from "responseHandler";
import WebConfig from "middlewares/WebConfig";
import { TextField } from "react-md";
import PhotoTags from "./Gallery/PhotoTags.tsx";
import Slider from "@material-ui/core/Slider";
import { Crop } from "react-image-crop";
import { usePhoenixForms } from "components/PhoenixFormsV4/PhoenixFormsProvider.tsx";
import IconButton from "components/Shared/IconButton.tsx";
import { MSelectOption } from "components/Shared/MSelect.tsx";
import { WindowContext, WindowProviderValue } from "components/Shared/WindowProvider.tsx";

export interface PhotoEditSaveRequest {
    Edits: PhotoEditSaveRequestEdit[]
}

interface PhotoEditSaveRequestEdit {
    Operation: number;
    Parameter: number | string;
}

interface PhotoToolProps {
  controlNumber: number,
  scrollableView: boolean,
  photosOverride: Photo[],
  getControlMethods: (cm: any) => void,
  multiSelect: boolean,
  onPhotosPopOutButtonHit: () => void,
  isPopOutWindow: boolean,
  editedPhotoIds: string[],
  addEditedPhotoId: (id: string) => void,
  customerSendEmailView: boolean,
  preselectedPhotoTags: string[],
  //state
  rights: UserRight[],
  token: string,
  caseHeader: CaseHeaderDto,
  completingCase: boolean,
  rejectingCase: boolean,
  escalatingCase: boolean,
  isFetchingPreferences: boolean,
  canEditPhotos: boolean,
  isFetchingHeader: boolean,
  //dispatch
  loadPreference: (code: string) => void,
  makeToast: (text: string, type: string) => void,
}

const rotationDirections = {
  right: -1,
  left: 1,
};

const maxWidthAndHeight = 800;

const GET_EDITING_ACTION_BUTTONS = "GET_EDITING_ACTION_BUTTONS";
const GET_REGULAR_ACTION_BUTTONS = "GET_REGULAR_ACTION_BUTTONS";
const GET_SETTINGS_ACTION_BUTTON = "GET_SETTINGS_ACTION_BUTTON";

const PhotoTool = (props: PhotoToolProps) => {

  const photoTagsInitializer = () => {
    if (props.preselectedPhotoTags && props.preselectedPhotoTags.length) {
      return props.preselectedPhotoTags.map(tag => ({ label: tag, value: tag }));
    }
    return [];
  };

  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(null);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<string[]>([]);
  const [isDisplayingUntaggedPhotos, setIsDisplayingUntaggedPhotos] = useState<boolean>(false);
  const [isFetchingPhotos, setIsFetchingPhotos] = useState<boolean>(true);
  const [photos, setPhotos] = useState<PhotoPropertiesPhotoSvc[]>(null);
  const [isSortingPhotos, setIsSortingPhotos] = useState<boolean>(false);
  const [deletedCasePhotos, setDeletedCasePhotos] = useState<PhotoPropertiesPhotoSvc[]>(null);
  const [isFetchingDeletedPhotos, setIsFetchingDeletedPhotos] = useState<boolean>(true);
  const [isDisplayingDeletedPhotos, setIsDisplayingDeletedPhotos] = useState<boolean>(false);
  const [isEditingPhoto, setIsEditingPhoto] = useState<boolean>(false);
  const [rotationAngle, setRotationAngle] = useState<number>(0);
  const [isEditingBrightness, setIsEditingBrightness] = useState<boolean>(false);
  const [isCropping, setIsCropping] = useState<boolean>(false);
  const [brightnessLevel, setBrightnessLevel] = useState<number>(0);
  const [newUserDescription, setNewUserDescription] = useState<string>(null);
  const [isSavingPhotoEdit, setIsSavingPhotoEdit] = useState<boolean>(false);
  const [isSavingDescriptionEdit, setIsSavingDescriptionEdit] = useState<boolean>(false);
  const [zoomPercent, setZoomPercent] = useState<number>(100);
  const [isSelectingMultiDelete, setIsSelectingMultiDelete] = useState<boolean>(false);
  const [isDeletingPhotos, setIsDeletingPhotos] = useState<boolean>(false);
  const [selectedTags, setSelectedTags] = useState<MSelectOption[]>(photoTagsInitializer);
  const [editedPhotoIds, setEditedPhotoIds] = useState<string[]>([]); //new window data is of type Object, but if it's set using the photo tool it will be an array of strings if it has a value

  const mounted = useRef<boolean>(true);
  const cardControlMethods = useRef<MCardControlMethods>();
  const cropRef = useRef<Crop>(null);
  const broadcastChannel = useRef<BroadcastChannel>(null);

  const dialog = useContext<DialogProviderValue>(DialogContext);
  const newWindow = useContext<WindowProviderValue>(WindowContext);
  const pnxSaveSubscribeCleanupRef = useRef<() => void>(null);

  const { reload: reloadPnxForms, save: savePnxForms, subscribeToOnSaveSuccess } = usePhoenixForms(); // called to force forms to reload when a photo is edited, deleted, or sorted

  const isSinglePhotoMode = selectedPhotoIndex !== null && selectedPhotoIndex !== undefined;

  const fetchAllPhotos = () => {
    setIsFetchingPhotos(true);
    setIsFetchingDeletedPhotos(true);
    getPhotos();
    getDeletedPhotos();
  }

  useEffect(() => {
    if (props.isPopOutWindow) {
      broadcastChannel.current = new BroadcastChannel(PHOTOS_BROADCAST_CHANNEL_NAME);
      broadcastChannel.current.onmessage = (event) => {
        if (event.data.type === PHOTOS_BROADCAST_RELOAD_PHOTOS) {
          fetchAllPhotos();
        }
      };
      broadcastChannel.current.onmessageerror = (event) => {
        console.error("BroadcastChannel error: ", event);
      };
    }
    else {
      pnxSaveSubscribeCleanupRef.current = subscribeToOnSaveSuccess(() => {
        reloadPnxForms(); // Reload forms after we trigger a save
      });
    }
    return () => {
      mounted.current = false;
      pnxSaveSubscribeCleanupRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (newWindow?.windowData) {
      setEditedPhotoIds(newWindow.windowData);
    }
    else if (props.editedPhotoIds) {
      setEditedPhotoIds(props.editedPhotoIds);
    }
    else {
      setEditedPhotoIds([]);
    }
  }, [newWindow?.windowData]);

  /*
    Previously we were subscribing to the PhoenixForms save complete event to reload the photos.
    But it looks like we refresh caseheaders after the forms are saved, and the useEffect here has a dependency on the caseheaders. This was causing getPhotos to fire off twice
    If we need to we can always re-subscribe to the event when the case forms are saved
  */
  useEffect(() => {
    //If there are photos provided, we don't need to fetch anything. If photos pop out window is active we don't want to do anything either
    if (!props.photosOverride && props.caseHeader?.policyInfo?.customerInfo?.customerNumber) {
      fetchAllPhotos();

      if (props.controlNumber) {
        //Load preferences
        if (!props.multiSelect) {
          props.loadPreference(PhotoTagSetting);
          props.loadPreference(PhotoButtonSetting);
          props.loadPreference(PhotoNumberingSetting);
          props.loadPreference(PhotoSizeSetting);
        }
      }
      else {
        setIsFetchingPhotos(false);
        setIsFetchingDeletedPhotos(false);
      }
    }
  }, [props.controlNumber, props.photosOverride, props.multiSelect, props.caseHeader]);

  useEffect(() => {
    if (props.getControlMethods)
      props.getControlMethods({
        setSelectedPhotoIndex: (index: number) => setSelectedPhotoIndex(index),
        getselectedPhotoIds: () => selectedPhotoIds,
      });
  }, [props.getControlMethods]);

  useEffect(() => {
    // When the tags are changed, deleted cases selected, or filtering untagged photos, remove the photoTool from single photo mode
    setSelectedPhotoIndex(null);
  }, [selectedTags, isDisplayingDeletedPhotos, isDisplayingUntaggedPhotos]);

  const getPhotos = () => {
    if (!isFetchingPhotos)
      setIsFetchingPhotos(true);
    if (photos)
      setPhotos(null);
    getCasePhotos(props.caseHeader.policyInfo.customerInfo.customerNumber, props.controlNumber, props.token,
      (photos) => {
        decodePhotoUris(photos);
        setPhotos(photos);
        setIsFetchingPhotos(false);
      },
      () => {
        props.makeToast("Failed to get case photos", "error");
        setIsFetchingPhotos(false);
      }
    );
  };

  const getDeletedPhotos = () => {
    if (!isFetchingDeletedPhotos)
      setIsFetchingDeletedPhotos(true);
    getCaseDeletedPhotos(props.caseHeader.policyInfo.customerInfo.customerNumber, props.controlNumber, props.token,
      (deletedPhotos) => {
        setDeletedCasePhotos(deletedPhotos);
        setIsFetchingDeletedPhotos(false);
      },
      () => {
        props.makeToast("Failed to get deleted photos", "error");
        setIsFetchingDeletedPhotos(false);
      }
    );
  };

  const resetZoom = () => { 
    setZoomPercent(100);
  };

  //The URIs on the photo properties have escape characters. decodeURI removes the unneded escaping
  const decodePhotoUris = (photos: PhotoPropertiesPhotoSvc[]) => {
    photos.forEach(photo => {
      photo.BucketUri = decodeURI(photo.BucketUri);
    });
  };

  const onGalleryPhotoClick = (e: Event, { index, photo }) => {
    e.stopPropagation();
    if (props.multiSelect || isSelectingMultiDelete) {
      if (selectedPhotoIds.includes(photo.photoid)) {
        setSelectedPhotoIds(selectedPhotoIds.filter(p => p !== photo.photoid));
      } else {
        setSelectedPhotoIds([...selectedPhotoIds, photo.photoid]);
      }
    }
    //Only have to do it this way if we're filtering by photo tags
    else if (selectedTags?.length) {
      setSelectedPhotoIndex(photos.findIndex(p => p.Id === photo.photoid));
    }
    else {
      changePhotoIndex(index);
    }
  };

  const changePhotoIndex = (i: number) => {
    setSelectedPhotoIndex(i);
    resetZoom();
  };

  const showDownloadPhotosDialog = () => {
    dialog.showYesNo(
      "Download All Photos",
      "Would you like to download photos with default or photo tag file names?",
      () => { downloadPhotos(); },
      () => { downloadPhotos(PhotoDownloadNamingConventions.PHOTO_TAG_NAMING); },
      "Default",
      "Photo Tags",
      { actions: [{ icon: "close", helpText: "Cancel", onClick: () => dialog.hide() }] });
  };

  const rotatePhoto = (direction: number) => {
    //Cropping while rotating can cause weird issues. Simpler to just turn cropping back off after rotating
    if (isCropping)
      setIsCropping(false);
    const maxAngle = 270;
    if (direction === rotationDirections.left)
      setRotationAngle(rotationAngle === 0 ? maxAngle : rotationAngle - 90);
    else
      setRotationAngle(rotationAngle === maxAngle ? 0 : rotationAngle + 90);
  };

  const onCropButtonHit = () => {
    setIsCropping(!isCropping);
  };

  const onBrightnessButtonHit = () => {
    if (isEditingBrightness) {
      setBrightnessLevel(0);
    }
    setIsEditingBrightness(!isEditingBrightness);
  };

  const isRenderingPopOutButton = photos && props.onPhotosPopOutButtonHit && !props.isPopOutWindow && !isEditingPhoto && props.canEditPhotos && !isSelectingMultiDelete;

  //Getting editing actions only returns editing actions. Regular actions returns non-editing. No type provided returns only settings
  const getActions = (type?: "GET_EDITING_ACTION_BUTTONS" | "GET_REGULAR_ACTION_BUTTONS" | "GET_SETTINGS_ACTION_BUTTON") => {
    if (props.multiSelect) return [];

    switch (type) {
      case GET_EDITING_ACTION_BUTTONS:
        if (isEditingPhoto && props.canEditPhotos) {
          return [
            {
              icon: "rotate_right",
              helpText: "Rotate 90 degrees clockwise",
              order: 1,
              onClick: () => rotatePhoto(rotationDirections.right),
            },
            {
              icon: "rotate_left",
              helpText: "Rotate 90 degrees counter-clockwise",
              order: 2,
              onClick: () => rotatePhoto(rotationDirections.left),
            },
            {
              icon: "exposure",
              helpText: "Adjust brightness",
              order: 3,
              onClick: onBrightnessButtonHit,
            },
            {
              icon: "crop",
              helpText: "Crop",
              order: 4,
              onClick: onCropButtonHit,
            }
          ];
        }
        else {
          return [];
        }
      case GET_REGULAR_ACTION_BUTTONS:
        const actions = [];
        const isRenderingMultiDeleteButton = !isDisplayingDeletedPhotos && !isSortingPhotos && !isSelectingMultiDelete && (selectedPhotoIndex === null || selectedPhotoIndex === undefined) && props.canEditPhotos;
        const isRenderingConfirmMultiDeleteButton = selectedPhotoIds && selectedPhotoIds.length > 0 && isSelectingMultiDelete;
        const isRenderingCancelMultiDelete = !isDisplayingDeletedPhotos && !isSortingPhotos && isSelectingMultiDelete;
        const isRenderingSortButton = props.canEditPhotos && selectedPhotoIndex === null && !isSortingPhotos && !isSelectingMultiDelete && !isDisplayingDeletedPhotos && !isDisplayingUntaggedPhotos && (!selectedTags || selectedTags?.length === 0);
        const isRenderingDownloadButton = !isSelectingMultiDelete && (selectedPhotoIndex === null || selectedPhotoIndex === undefined) && !isDisplayingDeletedPhotos && !isDisplayingUntaggedPhotos&& (!selectedTags || selectedTags?.length === 0);

        if (isRenderingPopOutButton) {
          actions.push({
            icon: "open_in_new",
            helpText: "Pop Out Photo Tool",
            order: 1,
            onClick: props.onPhotosPopOutButtonHit,
          })
        }
        if (isRenderingDownloadButton) {
          actions.push({
            icon: "cloud_download",
            helpText: "Download Photos",
            order: 4,
            onClick: () => showDownloadPhotosDialog()
          });
        }
        if (isRenderingMultiDeleteButton) {
          actions.push({
            icon: "library_add_check",
            helpText: "Select photos to delete",
            order: 5,
            onClick: () => { 
              setIsSelectingMultiDelete(true);
            }
          });
        }
        if (isRenderingConfirmMultiDeleteButton) {
          actions.push({
            icon: "delete",
            helpText: "Confirm multi-delete",
            order: 7,
            onClick: () => {
              dialog.showYesNo(
                "Delete Multiple Photos",
                "Are you sure you want to delete these photos?",
                () => {
                  deleteSelectedPhotos();
                },
              );
            },
          });
        }
        if (isRenderingCancelMultiDelete) {
          actions.push({
            icon: "dashboard",
            helpText: "Back to gallery",
            order: 6,
            onClick: () => { 
              setIsSelectingMultiDelete(false);
              setSelectedPhotoIds([]);
            }
          });
        }
        // onClick enables sorting as long as user is not a customer and not in singlephoto view
        if (isRenderingSortButton) {
          actions.push({
            icon: "sort",
            helpText: "Sort photos",
            order: 2,
            onClick: () => {
              setIsSortingPhotos(true);
            },
          });
        }
        if (isSortingPhotos) {
          actions.push({
            icon: "save",
            helpText: "Save sorted photos",
            order: 2,
            onClick: () => {
              onSavePhotosSortOrder(photos);
            },
          });
        }
        return actions;
      case GET_SETTINGS_ACTION_BUTTON:
        return [
          {
            icon: "settings",
            helpText: "Settings",
            order: 5,
            onClick: () => !settingsOpen && setSettingsOpen(true),
            className: settingsOpen && "active",
            children:
              (<PhotoSettings
                settingsOpen={settingsOpen}
                setSettingsOpen={() => setSettingsOpen(false)}
              />)
          }
        ];
        default:
          return [];
    }
  };

  const onSortEndCallback = (sortedPhotos: PhotoPropertiesPhotoSvc[]) => {
    setPhotos(sortedPhotos);
  };

  //If we're viewing untagged photos, we need to get the actual photo index
  const getActualPhotoIndex = () => {
    if (!isDisplayingUntaggedPhotos) {
      return selectedPhotoIndex;
    }
    let selectedUntaggedPhotoId = getUntaggedPhotos()[selectedPhotoIndex].Id;
    return photos.findIndex((p) => p.Id === selectedUntaggedPhotoId);
  };

  const getUpdatePhotoRequestBody = (photo: PhotoPropertiesPhotoSvc): PhotoPropertiesPhotoSvc => {
    let time = `/Date(${new Date().valueOf()})/`;
    return {
      ...photo,
      CaptureDate: time,
      LastModifiedDate: time,
      LastViewedDate: time
    };
  };

  const savePhoenixForms = () => {
    if (broadcastChannel.current) {
      broadcastChannel.current.postMessage({
        type: PHOTOS_BROADCAST_SAVE_PHOENIX_FORMS
      });
    }
    else {
      savePnxForms();
    }
  };

  const deleteSelectedPhotos =  () => {
    const bodyProperties = [];
    selectedPhotoIds.forEach((id) => {
      const photo = photos.find((p) => p.Id === id);
      if (photo) {
        bodyProperties.push(getUpdatePhotoRequestBody(photo));
      }
    });
    setIsDeletingPhotos(true);
    updateMultiplePhotoProperties(props.caseHeader.policyInfo.customerInfo.customerNumber, props.controlNumber, props.token, true, { PhotoProperties: bodyProperties },
      () => {
        props.makeToast("Photos deleted successfully!", "success");
        setIsDeletingPhotos(false);
        setIsSelectingMultiDelete(false);
        setSelectedPhotoIds([]);
        fetchAllPhotos();
        savePhoenixForms();
      },
      () => {
        props.makeToast("Photos failed to delete, please try again", "error");
        setIsDeletingPhotos(false);
      }
    );
  };

  //The verbiage here could be a bit confusing. Since "deleting" a photo is actually just updating properties on the photo, it's always being updated rather than deleted.
  const updateSelectedPhotoProperties = (newDescription?: string, isDeleting?: boolean, successCallback?: (newPhotoProperties: PhotoPropertiesPhotoSvc) => void, errorCallback?: () => void) => {
    if (isSinglePhotoMode) {
      //A photo can be edited while viewing untagged photos, so have to make sure we have the correct photo Id
      let actualPhotoIndex = getActualPhotoIndex();

      let photosCopy = { ...photos };
      if (newDescription)
        photosCopy[actualPhotoIndex].UserDescription = newDescription;

      const requestBody = getUpdatePhotoRequestBody(photosCopy[actualPhotoIndex]);

      updatePhotoProperties(props.caseHeader.policyInfo.customerInfo.customerNumber, props.controlNumber, photos[actualPhotoIndex].Id, props.token, requestBody, isDeleting,
        (newPhotoProperties) => {
          successCallback?.(newPhotoProperties);
        },
        () => {
          props.makeToast(`Error ${isDeleting ? "deleting" : "updating"} photo, please try again`, "error");
          errorCallback?.();
        }
      );
    }
  };

  const onPhotoDeleteConfirmed = () => {
    setIsSavingPhotoEdit(true);
    updateSelectedPhotoProperties(undefined, true,
      () => {
        props.makeToast("Photo deleted successfully!", "success");
        let actualPhotoIndex = getActualPhotoIndex();
        setSelectedPhotoIndex(() => null);
        setPhotos((prevPhotos) => prevPhotos.filter(p => p.Id !== prevPhotos[actualPhotoIndex].Id));
        getDeletedPhotos();
        setIsSavingPhotoEdit(false);
        savePhoenixForms();
      },
      () => {
        props.makeToast("Photo failed to delete, please try again", "error");
        setIsSavingPhotoEdit(false);
      }
    );
  };

  const showCancelEditingDialog = (onCancelCallback?: () => void) => {
    dialog.showYesNo("Cancel Editing", "Cancel editing and discard changes?",
      () => {
        onPhotoEditCancel();
        if (onCancelCallback)
          onCancelCallback();
      });
  };

  const onPhotoDeleteHit = () => {
    dialog.showYesNo("Delete Photo", "Are you sure you'd like to delete this photo?", onPhotoDeleteConfirmed);
  };

  const clearEditingState = () => {
    setIsEditingPhoto(false);
    setBrightnessLevel(0);
    setRotationAngle(0);
    setIsCropping(false);
    cropRef.current = null;
  };

  const onPhotoEditCancel = () => {
    clearEditingState();
  };

  const onEditButtonHit = () => {
    let actualPhotoIndex = getActualPhotoIndex();
    if (isEditingPhoto) {
      if (rotationAngle || brightnessLevel || cropRef.current) {
        showCancelEditingDialog();
      }
      else {
        setIsEditingPhoto(false);
      }
    }
    else {
      setIsEditingPhoto(true);
      resetZoom();
      if (selectedPhotoIndex)
        setNewUserDescription(photos[actualPhotoIndex].UserDescription);
    }
  };

  const getCropCoordinates = () => {
    let topLeftX = Math.trunc(cropRef.current.x);
    let topLeftY = Math.trunc(cropRef.current.y);
    let bottomRightX = Math.trunc(cropRef.current.x + cropRef.current.width);
    let bottomRightY = Math.trunc(cropRef.current.y + cropRef.current.height);

    //If the photo was bigger than the max, it was halved so that we can crop without having to use the full size image. Because of this we have to multiply the coordinates by 2
    if (photos[selectedPhotoIndex].ImageDimensions.height > maxWidthAndHeight || photos[selectedPhotoIndex].ImageDimensions.width > maxWidthAndHeight) {
      topLeftX *= 2;
      topLeftY *= 2;
      bottomRightX *= 2;
      bottomRightY *= 2;
    }

    return `${topLeftX},${topLeftY},${bottomRightX},${bottomRightY}`;
  };

  const onEditSaveHit = () => {
    //In case we were editing an untagged photo
    let actualPhotoIndex = getActualPhotoIndex();

    setIsSavingPhotoEdit(true);
    setIsSavingDescriptionEdit(true);
    setIsEditingPhoto(false);

    let requestBody: PhotoEditSaveRequest = {
      Edits: [],
    };

    if (rotationAngle || brightnessLevel || cropRef.current) {
      if (rotationAngle) {
        requestBody.Edits.push({
          Operation: photoEditOperationTypes.ROTATE,
          Parameter: rotationAngle,
        });
      }
      if (brightnessLevel) {
        requestBody.Edits.push({
          Operation: photoEditOperationTypes.BRIGHTNESS,
          Parameter: brightnessLevel,
        });
      }
      //The default is a percentage for the original, but once it's being edited, it changes to px. Seems like the easiest way to check if it's the default crop object
      if (cropRef.current && cropRef.current.unit !== '%') {
        requestBody.Edits.push({
          Operation: photoEditOperationTypes.CROP,
          Parameter: getCropCoordinates(),
        });
      }
    }
    //Save photo edits if there are any
    if (requestBody.Edits.length) {
      savePhotoEdit(
        props.caseHeader.policyInfo.customerInfo.customerNumber,
        props.controlNumber,
        photos[actualPhotoIndex].Id,
        props.token,
        requestBody,
        (newPhotoProperties) => {
          props.makeToast("Photo edited successfully!", "success");
          delete newPhotoProperties.UserDescription;
          getPhotos();
          setIsSavingPhotoEdit(false);
          props.addEditedPhotoId?.(photos[actualPhotoIndex].Id);
          setEditedPhotoIds((prev) => [...prev, photos[actualPhotoIndex].Id]);
          if (broadcastChannel.current) {
            broadcastChannel.current.postMessage({
              type: PHOTOS_BROADCAST_PHOTO_EDITED_TYPE,
              payload: [...editedPhotoIds, photos[actualPhotoIndex].Id],
            });
          }
          clearEditingState();
        },
        () => {
          props.makeToast("Error editing photo, please try again", "error");
          setIsSavingPhotoEdit(false);
        }
      );
    }
    else {
      setIsSavingPhotoEdit(false);
    }

    //Save user description if there is one
    if (newUserDescription && newUserDescription !== photos[actualPhotoIndex].UserDescription) {
      updateSelectedPhotoProperties(newUserDescription, false,
        (newPhotoProperties) => {
          setIsEditingPhoto(false);
          setNewUserDescription(null);
          setIsSavingDescriptionEdit(false);
          setPhotos(prevStatePhotos => prevStatePhotos.map(p => {
            if (p.Id !== newPhotoProperties.Id)
              return { key: p.Id, ...p };
            else {
              return { key: newPhotoProperties.Id, ...p, UserDescription: newPhotoProperties.UserDescription };
            }
          }));
          props.makeToast("Photo description saved successfully!", "success");
        });
    }
    else {
      setIsSavingDescriptionEdit(false);
    }
  };

  const onGalleryButtonHit = () => {
    if (isEditingPhoto)
      showCancelEditingDialog(() => setSelectedPhotoIndex(null));
    else {
      setSelectedPhotoIndex(null);
      resetZoom();
    }
      
  };

  const onUserDescriptionChange = (newValue) => {
    setNewUserDescription(newValue);
  };

  const getOptionsString = () => {
    let options = "";
    if (rotationAngle) //rotation angle can be 0, but if it's 0 we don't want to send any rotation information anyways
      options += `Rotate;${rotationAngle}`;
    if (brightnessLevel !== 0) {
      options += `${options.length ? "_" : ""}Brightness;${brightnessLevel}`;
    }
    return options;
  };

  const getImagePreviewUrl = () => {
    let imageWidth: number;
    let imageHeight: number;

    //Photos can be edited while viewing untagged photos, so we need to make sure we have the correct index
    let actualPhotoIndex = getActualPhotoIndex();

    if (photos[actualPhotoIndex].ImageDimensions.width > maxWidthAndHeight || photos[actualPhotoIndex].ImageDimensions.height > maxWidthAndHeight) {
      imageWidth = Math.trunc(photos[actualPhotoIndex].ImageDimensions.width / 2);
      imageHeight = Math.trunc(photos[actualPhotoIndex].ImageDimensions.height / 2);
    }
    else {
      imageWidth = photos[actualPhotoIndex].ImageDimensions.width;
      imageHeight = photos[actualPhotoIndex].ImageDimensions.height;
    }
    //by adding time as a random query parameter, it forces the photos to refresh when re-loading the component
    return `${WebConfig.photoService}/${photos[actualPhotoIndex].BucketUri}/${photos[actualPhotoIndex].Id}/edits/preview?options=${getOptionsString()}&height=${imageHeight}&width=${imageWidth}&SSO=${props.token}&rnd=${new Date()}&t=${new Date().valueOf().toString()}`;
  };

  const setNewCrop = (newCrop: Crop) => {
    cropRef.current = newCrop;
  };

  const addSelectedTag = (tag: string) => {
    if (!selectedTags.find(t => t.value === tag)) {
      const newSelectedTags = [...selectedTags, { label: tag, value: tag }];
      setSelectedTags(newSelectedTags);
      setSelectedPhotoIndex(null);
    }
  }

  const getFields = (photos: PhotoPropertiesPhotoSvc[]) => {
    let fields = [];
    if (isSortingPhotos) {
      fields.push({
        key: "is-sorting-photos-mgallery",
        control:
          <MGallery
            photos={photos}
            sortingPhotos={isSortingPhotos}
            onSortEndCallback={onSortEndCallback}
            isDisplayingDeletedPhotos={isDisplayingDeletedPhotos}
            editedPhotoIds={editedPhotoIds}
            multiSelect={props.multiSelect}
            selectedPhotoTags={selectedTags.map(tag => tag.value)}
          />,
      });
    }
    else if (isSelectingMultiDelete) {
      fields.push({
        key: "photo-tool-multi-delete",
        control:
          <MGallery
            photos={isDisplayingUntaggedPhotos ? getUntaggedPhotos() : photos}
            onElementClick={onGalleryPhotoClick}
            selectedPhotoIds={selectedPhotoIds}
            multiSelect
            editedPhotoIds={editedPhotoIds}
            selectedPhotoTags={selectedTags.map(tag => tag.value)}
            addSelectedTag={isDisplayingUntaggedPhotos ? addSelectedTag : null}
          />,
      });
    }
    else if (selectedPhotoIndex !== null && !props.multiSelect) {
      fields.push({
        key: "photo-tool-single-photo",
        control:
          <SinglePhoto
            photoCount={photos.length}
            onPhotoIndexChange={changePhotoIndex}
            selectedPhotoIndex={selectedPhotoIndex}
            isEditingPhoto={isEditingPhoto}
            imgSource={
              !isEditingPhoto ? `${WebConfig.photoService}/${photos[selectedPhotoIndex].BucketUri}/${photos[selectedPhotoIndex].Id}?SSO=${props.token}`
                : getImagePreviewUrl()}
            isCropping={isCropping}
            setCrop={setNewCrop}
            photoWidth={photos[selectedPhotoIndex].ImageDimensions.width * zoomPercent / 100}
            photoHeight={photos[selectedPhotoIndex].ImageDimensions.height * zoomPercent / 100}
            editedPhotoIds={editedPhotoIds}
            photoId={photos[selectedPhotoIndex].Id}
          />,
      });
      fields.push({
        key: "photo-tool-single-photo-footer",
        control:
          <div className="single-photo-footer">
            <div className="photo-tool-single-photo-footer-tags-label">
              Tags
            </div>
            <div className="photo-tool-single-photo-footer-tags">
              <PhotoTags
                photoTagsArray={photos[selectedPhotoIndex].Tags}
                horizontal
                addSelectedPhotoTag={addSelectedTag}
              />
            </div>
            {props.canEditPhotos ?
              <div>
                <div className="photo-tool-single-photo-description-label">
                  Description
                </div>
                <TextField
                  id="photo-tool-single-photo-description-text-field"
                  value={newUserDescription ?? photos[selectedPhotoIndex].UserDescription ?? ""} //passing in an empty string when the user description is null makes sure the component updates properly
                  onChange={onUserDescriptionChange}
                  disabled={!isEditingPhoto}
                  rows={1}
                  maxRows={3}
                  inputClassName="photo-description-text-field"
                  active={isEditingPhoto}
                />
              </div> : <></>}
          </div>
      });
    }
    else if (!isSortingPhotos && photos) {
      fields.push({
        key: "mgallery",
        control:
          <MGallery
            photos={photos}
            onElementClick={onGalleryPhotoClick}
            selectedPhotoIds={selectedPhotoIds}
            multiSelect={props.multiSelect}
            sortingPhotos={isSortingPhotos}
            isDisplayingDeletedPhotos={isDisplayingDeletedPhotos}
            editedPhotoIds={editedPhotoIds}
            customerSendEmailView={props.customerSendEmailView}
            addSelectedTag={addSelectedTag}
            selectedPhotoTags={selectedTags.map(tag => tag.value)}
          />,
      });
    }
    else {
      fields.push((<div />));
    }
    return fields;
  };

  const onSavePhotosSortOrder = (photos: PhotoPropertiesPhotoSvc[]) => {
    if (!photos || photos.length === 0) {
      setIsSortingPhotos(false);
      return;
    }
    let reorderedIds = [];
    photos.forEach(photo => {
      reorderedIds.push(photo.Id);
    });
    savePhotosSortOrder(
      props.controlNumber,
      reorderedIds,
      mounted,
      () => {
        props.makeToast("Sort order saved successfully!", "success");
      }
    );
    setIsSortingPhotos(false);
  };

  const downloadPhotos = (namingConvention: string = PhotoDownloadNamingConventions.DEFAULT_PHOTO_NAMING) => {

    downloadCasePhotos(props.caseHeader.policyInfo.customerInfo.customerNumber, props.controlNumber, namingConvention, props.token, (blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${props.controlNumber}_photos.zip`;
      document.body.appendChild(a); // must append element to the DOM otherwise this won't work in firefox
      a.click();
      a.remove();  // remove element from the dom 
    });
  };

  if (isFetchingPhotos || isFetchingDeletedPhotos || props.isFetchingPreferences) return <MSpinner />;

  const untaggedPhotosWarningButtonHit = () => {
    setSelectedPhotoIndex(null);
    if (isDisplayingDeletedPhotos)
      setIsDisplayingDeletedPhotos(false);
    setIsDisplayingUntaggedPhotos(!isDisplayingUntaggedPhotos);
  };

  const deletedPhotosButtonHit = () => {
    if (isDisplayingUntaggedPhotos)
      setIsDisplayingUntaggedPhotos(false);
    setSelectedPhotoIndex(null);
    setIsDisplayingDeletedPhotos(!isDisplayingDeletedPhotos);
  };

  const onBrightnessSliderDragEnd = (event: React.ChangeEvent<{}>, brightnessNumber: number) => {
    setBrightnessLevel(brightnessNumber);
  };

  const getUntaggedPhotos = () => {
    return photos?.filter(photo => !photo.Tags || photo.Tags.length === 0);
  };

  const onPhotoRestoreHit = () => {
    setIsSavingPhotoEdit(true);

    restorePhotoFromTrash(props.caseHeader.policyInfo.customerInfo.customerNumber, props.controlNumber, props.token, { PhotoProperties: [{...deletedCasePhotos[selectedPhotoIndex]}] },
      (newPhotoProperties) => {
        props.makeToast("Photo successfully restored!", "success");
        const photoIndex = selectedPhotoIndex;
        const newDeletedPhotos = deletedCasePhotos.filter(dcp => dcp.Id !== deletedCasePhotos[photoIndex].Id);
        setSelectedPhotoIndex(null);
        setDeletedCasePhotos(newDeletedPhotos);
        setPhotos((photos) => [...photos, newPhotoProperties[0]]); //restoring a photo returns an array
        if(!newDeletedPhotos || !newDeletedPhotos.length)
          setIsDisplayingDeletedPhotos(false);
        setIsSavingPhotoEdit(false);
        savePhoenixForms();
      },
      () => {
        props.makeToast("Photo was not successfully restored, please try again", "error");
        setIsSavingPhotoEdit(false);
      }
    );
  };

  const onZoomInButtonClick = () => {
    if (zoomPercent < 250) {
      setZoomPercent(zp => zp + 25);
    }
  };

  const onZoomOutButtonClick = () => {
    if (zoomPercent > 100) {
      setZoomPercent(zp => zp - 25);
    }
  };

  const getMappedTags = (photos: PhotoPropertiesPhotoSvc[]) => {
    //@ts-ignore
    return [...new Set(
      photos.flatMap(photo => photo.Tags)
    )].sort((a, b) => a.localeCompare(b)) //I tried to use this as a sort function in MSelect where we have the tags drop down but it was not working the way I expected. Easier to just sort it here
  }

  // the type is MSelectOption[] but I'm throwing | any in there to avoid typescript yelling about it
  const onTagSelectionChanged = (newValues: MSelectOption[] | any) => {
    setSelectedTags(newValues);
  };

  const isRenderingUntaggedWarning = !isSelectingMultiDelete && ((props.canEditPhotos && getUntaggedPhotos().length && !isEditingPhoto) || isDisplayingUntaggedPhotos);
  const isRenderingBrightnessSlider = isEditingPhoto && isEditingBrightness;
  const isRenderingDeletedPhotosButton = !isSelectingMultiDelete && !isEditingPhoto && props.canEditPhotos;
  const isRenderingSearch = !isSelectingMultiDelete && !isEditingPhoto && (selectedPhotoIndex === null || selectedPhotoIndex === undefined);
  const isRenderingZoomSlider = isSinglePhotoMode && !isEditingPhoto;
  const isRenderingEditButton = isSinglePhotoMode && props.canEditPhotos && !isDisplayingDeletedPhotos;
  const isRenderingDeletePhotoButton = isSinglePhotoMode && !isEditingPhoto && props.canEditPhotos && !isDisplayingDeletedPhotos;
  const isRenderingRestorePhotoButton = isSinglePhotoMode && props.canEditPhotos && isDisplayingDeletedPhotos && !isSavingPhotoEdit;

  const editButton = (
    <IconButton
      icon={isEditingPhoto ? "edit_off" : "edit"}
      onClick={onEditButtonHit}
      tooltip={isEditingPhoto ? "Cancel Edits" : "Edit Photo"}
    />)

  return (
    <div className="photo-tool">
      <MCard
        className={isEditingPhoto ? "photo-tool-mcard-edit-mode" : "photo-tool-mcard"}
        title={
          `${isEditingPhoto ? "Editing Photo" :
            isDisplayingDeletedPhotos ? `Deleted Photos (${deletedCasePhotos?.length})` :
              isDisplayingUntaggedPhotos ? `Untagged Photos (${getUntaggedPhotos().length})` :
                `Photos (${photos?.length || "0"})`}`
        }
        fields={getFields(
          props.photosOverride ??
            isDisplayingDeletedPhotos ? deletedCasePhotos :
            isDisplayingUntaggedPhotos ? getUntaggedPhotos() :
              photos
        )}
        headerContent={
          <div className="photo-tool-header-container">
            <div className="photo-tool-header photo-tool-header-left">
              {isRenderingDeletedPhotosButton &&
                <IconButton
                  onClick={deletedPhotosButtonHit}
                  icon="folder_delete"
                  tooltip={deletedCasePhotos?.length > 0 ? !isDisplayingDeletedPhotos ? "Display deleted photos" : "Show case photos" : "No deleted photos"}
                  disabled={deletedCasePhotos?.length < 1 || isFetchingDeletedPhotos}
                />}
              {isRenderingUntaggedWarning &&
                <IconButton
                  onClick={untaggedPhotosWarningButtonHit}
                  icon="warning"
                  tooltip={`${getUntaggedPhotos().length ? !isDisplayingUntaggedPhotos ? `Filter untagged photos (${getUntaggedPhotos().length})` : "Disable untagged photos filter" : "No untagged photos to filter"}`}
                />}
            </div>
            <div className="photo-tool-header photo-tool-header-center">
              {isRenderingBrightnessSlider &&
                <Slider
                  min={-50}
                  max={50}
                  defaultValue={0}
                  color="primary"
                  className="photo-tool-brightness-slider"
                  marks={[{ value: -25, label: "-25%" }, { value: 25, label: "25%" }]}
                  onChangeCommitted={onBrightnessSliderDragEnd}
                  valueLabelDisplay="off"
                />}
              {isRenderingEditButton && !isEditingPhoto && editButton} {/* If they're editing the edit button goes here, otherwise it goes in the right side container */}
              {isRenderingZoomSlider &&
                <div className="photo-tool-zoom-slider-container">
                  <IconButton
                    icon="zoom_out"
                    onClick={onZoomOutButtonClick}
                    tooltip="Zoom Out"
                  />
                  <Slider
                    min={100}
                    max={250}
                    value={zoomPercent}
                    color="primary"
                    className="photo-tool-zoom-slider"
                    marks={[{ value: 100, label: "100%" }, { value: 125, label: "" }, { value: 150, label: "" }, { value: 175, label: "" }, { value: 200, label: "" }, { value: 225, label: "" }, { value: 250, label: "250%" }]}
                    step={25}
                    onChangeCommitted={(e, value) => setZoomPercent(value as number)}
                    valueLabelDisplay="off"
                  />
                  <IconButton
                    icon="zoom_in"
                    onClick={onZoomInButtonClick}
                    tooltip="Zoom In"
                  />
                </div>}
              {isRenderingDeletePhotoButton &&
                <IconButton
                  icon="delete"
                  onClick={onPhotoDeleteHit}
                  tooltip="Delete Photo"
                />}
              {isRenderingRestorePhotoButton &&
                <IconButton
                  icon="delete_sweep"
                  onClick={onPhotoRestoreHit}
                  tooltip="Restore Photo"
                />}
              {isRenderingSearch &&
                <MSelect
                  className="photo-tool-tag-select"
                  onChange={onTagSelectionChanged}
                  options={getMappedTags(isDisplayingUntaggedPhotos ? getUntaggedPhotos() : photos)}
                  value={selectedTags}
                  placeholder="Filter by Tags"
                  isClearable
                  isMulti
                  enableAllOption={false}
                />}
              {isEditingPhoto &&
                <>
                  {getActions(GET_EDITING_ACTION_BUTTONS).map(a => <IconButton key={a.id} icon={a.icon} onClick={a.onClick} tooltip={a.helpText} disabled={a.disabled} />)}
                </>}
            </div>
            <div className="photo-tool-header photo-tool-header-right">
              {!isEditingPhoto && getActions(GET_REGULAR_ACTION_BUTTONS).map(a => <IconButton key={a.id} icon={a.icon} onClick={a.onClick} tooltip={a.helpText} disabled={a.disabled} />)}
              {isRenderingEditButton && isEditingPhoto && editButton}
              {isEditingPhoto && <IconButton icon="save" onClick={onEditSaveHit} tooltip="Save Changes" />}
              {isSinglePhotoMode &&
                <IconButton
                  icon="dashboard"
                  onClick={onGalleryButtonHit}
                  tooltip="Back to Gallery"
                />}
            </div>
          </div>}
        actions={getActions(GET_SETTINGS_ACTION_BUTTON)} // Get only settings through the actions prop because it renders children which isn't supported through IconButton right now
        getControlMethods={controlMethods => cardControlMethods.current = controlMethods}
        onlyEditable
        isFetching={isFetchingPhotos || isFetchingDeletedPhotos || isSavingPhotoEdit || isSavingDescriptionEdit || isDeletingPhotos}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  rights: state.user.profile.rights,
  token: state.user.profile.token,
  caseHeader: state.caseForms.caseHeader,
  completingCase: state.qualityAssurance.qaVariables.completingCase,
  rejectingCase: state.qualityAssurance.qaVariables.rejectingCase,
  escalatingCase: state.qualityAssurance.qaVariables.escalatingCase,
  isFetchingPreferences: state.preferences.isFetching,
  canEditPhotos: userHasRight(PHOTO_WRITE_UNRESTRICTED)(state),
  isFetchingHeader: state.caseForms.fetchingHeader,
});

const mapDispatchToProps = dispatch => ({
  loadPreference(code) {
    dispatch(loadPreference(code));
  },
  makeToast(text, type) {
    responseHandler.customToast(text, type, dispatch);
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(PhotoTool);
